import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { format, formatDistanceToNow, startOfDay, subDays } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import ReactMarkdown from "react-markdown";
import Masonry from "react-masonry-css";
import remarkBreaks from "remark-breaks";
import { trpc } from "@/app/api/trpcClient";
import { GitHubLink } from "@/app/components/GitHubLink";
import { TeamBadgeDropdown } from "@/app/components/TeamBadgeDropdown";
import { Avatar, AvatarFallback, AvatarImage } from "@/app/components/ui/avatar";
import { Button } from "@/app/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/app/components/ui/tooltip";
import { useDagStore } from "@/app/hooks/useDagStore";
import { useDataSync } from "@/app/hooks/useDataSync";
import { formatReplyCount, parseSlackMessageToMarkdown } from "@/app/utils/slackParser";
import type { SlackMessage, SlackUser } from "@/src/core/slack";

export const Route = createFileRoute("/slack-news")({
  component: SlackNewsPage,
});

function SlackNewsPage() {
  // Ensure data is synced before using the store
  useDataSync();

  const navigate = useNavigate();
  const teamMembers = useDagStore((state) => state.teamMembersFiltered);
  const slackFilters = useDagStore((state) => state.slackFilters);
  const setSlackFilters = useDagStore((state) => state.setSlackFilters);
  const excludedTeams = useDagStore((state) => state.excludedTeams);

  const {
    data: messagesGrouped,
    isLoading,
    error,
  } = trpc.slack.messagesGroupedByUser.useQuery({
    channelId: "daily-updates",
    limit: 200,
    startDate: slackFilters.startDate,
    endDate: slackFilters.endDate,
    teamMemberType: slackFilters.teamMemberType,
    roleFilter: slackFilters.roleFilter,
    excludedTeams: Array.from(excludedTeams),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">Loading Slack messages...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-red-500">Error loading messages: {error.message}</div>
      </div>
    );
  }

  if (!messagesGrouped || messagesGrouped.length === 0) {
    const handleSeeEarlier = () => {
      const newStartDate = slackFilters.startDate
        ? startOfDay(subDays(slackFilters.startDate, 1))
        : startOfDay(subDays(new Date(), 1));

      setSlackFilters({
        ...slackFilters,
        startDate: newStartDate,
      });
    };

    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <div className="text-gray-500">No Slack messages found for the selected date range.</div>
        <Button onClick={handleSeeEarlier} variant="outline">
          See Earlier (Go Back 1 Day)
        </Button>
      </div>
    );
  }

  // Function to find team member by Slack user ID
  const findTeamMemberBySlackId = (slackUserId: string) => {
    return teamMembers.find((member) => member.slackUserId === slackUserId);
  };

  // Function to handle avatar click - navigate to member profile page
  const handleAvatarClick = (slackUser: SlackUser) => {
    const teamMember = findTeamMemberBySlackId(slackUser.id);
    if (teamMember) {
      void navigate({ to: "/member/$memberId", params: { memberId: teamMember.id } });
    }
  };

  return (
    <div className="h-full overflow-x-hidden">
      <div className="px-6 pt-6 pb-4">
        <h1 className="text-3xl font-bold">💬 Slack News</h1>
        <p className="text-gray-600 mt-2">Recent messages from the #daily-updates channel</p>
      </div>

      <Masonry
        breakpointCols={{
          default: 4,
          1600: 3,
          1100: 2,
          640: 1,
        }}
        className="flex w-auto px-6 pb-6"
        columnClassName="bg-clip-padding pl-4 first:pl-0"
      >
        {messagesGrouped.map((group: { user: SlackUser; messages: SlackMessage[] }) => {
          const teamMember = findTeamMemberBySlackId(group.user.id);

          return (
            <Card key={group.user.id} className="overflow-hidden mb-4">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <Avatar
                    className={`h-10 w-10 ${teamMember ? "cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all" : ""}`}
                    onClick={() => handleAvatarClick(group.user)}
                  >
                    {group.user.avatar ? (
                      <AvatarImage
                        src={group.user.avatar}
                        alt={group.user.displayName || group.user.username}
                      />
                    ) : null}
                    <AvatarFallback>
                      {(group.user.displayName || group.user.username || "U")
                        .slice(0, 2)
                        .toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg truncate flex items-center gap-2">
                      {group.user.displayName || group.user.realName || group.user.username}
                      {teamMember?.team && <TeamBadgeDropdown team={teamMember.team} />}
                    </CardTitle>
                    {group.user.title && (
                      <p className="text-sm text-gray-500 truncate">{group.user.title}</p>
                    )}
                  </div>
                  <div className="text-sm text-gray-500 whitespace-nowrap">
                    {group.messages.length} message{group.messages.length !== 1 ? "s" : ""}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-3">
                  {group.messages.slice(0, 5).map((message: SlackMessage) => {
                    const parsedContent = parseSlackMessageToMarkdown(message.text || "");
                    const replyText = formatReplyCount(message.replyCount);

                    return (
                      <div key={message.id} className="py-2">
                        <div className="flex items-baseline justify-between mb-1 gap-2">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="text-xs text-gray-500 cursor-help">
                                {message.messageDate
                                  ? formatDistanceToNow(new Date(message.messageDate), {
                                      addSuffix: true,
                                    })
                                  : "Unknown time"}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {message.messageDate
                                ? (() => {
                                    const viewerTz =
                                      Intl.DateTimeFormat().resolvedOptions().timeZone;
                                    const senderTz = group.user.timezone;
                                    const isSameTimezone = senderTz && viewerTz === senderTz;

                                    if (isSameTimezone) {
                                      return (
                                        <div>{format(new Date(message.messageDate), "PPpp")}</div>
                                      );
                                    }

                                    return (
                                      <div className="space-y-1">
                                        <div>
                                          <span className="font-semibold">Your time:</span>{" "}
                                          {format(new Date(message.messageDate), "PPpp")}
                                        </div>
                                        {senderTz && (
                                          <div>
                                            <span className="font-semibold">Sender's time:</span>{" "}
                                            {formatInTimeZone(
                                              new Date(message.messageDate),
                                              senderTz,
                                              "PPpp",
                                            )}{" "}
                                            ({senderTz})
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })()
                                : "Unknown date"}
                            </TooltipContent>
                          </Tooltip>
                          {replyText && (
                            <span className="text-xs text-blue-600 whitespace-nowrap">
                              {replyText}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-700 prose prose-sm max-w-none">
                          {parsedContent ? (
                            <ReactMarkdown
                              remarkPlugins={[remarkBreaks]}
                              components={{
                                h3: ({ children }) => (
                                  <h3 className="text-base font-semibold text-gray-800 mt-2 mb-1">
                                    {children}
                                  </h3>
                                ),
                                ul: ({ children }) => (
                                  <ul className="list-disc pl-4 space-y-1">{children}</ul>
                                ),
                                ol: ({ children }) => (
                                  <ol className="list-decimal pl-4 space-y-1">{children}</ol>
                                ),
                                li: ({ children }) => (
                                  <li className="text-gray-700">{children}</li>
                                ),
                                p: ({ children }) => <p className="mt-2">{children}</p>,
                                a: ({ href, children }) => {
                                  if (!href) {
                                    return <a>{children}</a>;
                                  }
                                  return <GitHubLink href={href}>{children}</GitHubLink>;
                                },
                              }}
                            >
                              {parsedContent}
                            </ReactMarkdown>
                          ) : (
                            <span className="italic text-gray-400">No text content</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {group.messages.length > 5 && (
                    <div className="text-sm text-gray-500 italic pl-4">
                      ... and {group.messages.length - 5} more messages
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </Masonry>
    </div>
  );
}
